import { useState, useMemo } from 'react'
import { C, getBuilding, printHTML } from './utils'

function QRCard({unit, selected, onToggle}) {
  return (
    <div onClick={()=>onToggle(unit.id)} style={{
      border:`2px solid ${selected?C.navy:C.border}`,
      borderRadius:10,padding:'10px 12px',cursor:'pointer',
      background:selected?'#e8f0fa':'#fff',transition:'all .15s',
      display:'flex',alignItems:'center',gap:10
    }}>
      <div style={{width:20,height:20,borderRadius:'50%',border:`2px solid ${selected?C.navy:C.border}`,
        background:selected?C.navy:'transparent',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
        {selected&&<span style={{color:'#fff',fontSize:12}}>✓</span>}
      </div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:11,color:C.muted}}>{unit.area}</div>
        <div style={{fontWeight:700,fontSize:13,color:C.text,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{unit.location}</div>
        <div style={{fontSize:11,color:C.steel,fontFamily:'monospace'}}>{unit.filter_type==='washable'?'♻️ Washable':unit.size} × {unit.qty}</div>
      </div>
    </div>
  )
}

export default function QRModal({units, onClose}) {
  const [selected, setSelected] = useState(new Set())
  const [search, setSearch] = useState('')
  const [building, setBuilding] = useState('all')

  const buildings = useMemo(()=>[...new Set(units.map(u=>getBuilding(u.area)))],[units])

  const filtered = units.filter(u=>{
    if(search&&!`${u.area} ${u.location}`.toLowerCase().includes(search.toLowerCase())) return false
    if(building!=='all'&&getBuilding(u.area)!==building) return false
    return true
  })

  function toggleUnit(id) {
    setSelected(prev=>{
      const s=new Set(prev)
      s.has(id)?s.delete(id):s.add(id)
      return s
    })
  }
  function selectAll() { setSelected(new Set(filtered.map(u=>u.id))) }
  function clearAll() { setSelected(new Set()) }

  function printQRs() {
    const selectedUnits = units.filter(u=>selected.has(u.id))
    if(!selectedUnits.length) return

    const cards = selectedUnits.map(u=>{
      const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(`https://hvac.khalbobov.org/?unit=${u.id}`)}`
      return `
        <div class="qr-card">
          <img src="${qrUrl}" alt="QR" width="160" height="160"/>
          <div class="unit-name">${u.location}</div>
          <div class="unit-area">${u.area}${u.description?` · ${u.description}`:''}</div>
          <div class="unit-filter">${u.filter_type==='washable'?'♻️ Washable':`${u.size} × ${u.qty}`}</div>
        </div>`
    }).join('')

    const html = `<!DOCTYPE html><html><head><title>HVAC Filter QR Codes</title>
    <style>
      body{font-family:system-ui,sans-serif;margin:0;padding:16px;background:#fff}
      h2{color:#0f2237;font-size:16px;margin-bottom:16px;padding-bottom:8px;border-bottom:2px solid #0f2237}
      .grid{display:grid;grid-template-columns:repeat(4,1fr);gap:16px}
      .qr-card{border:1px solid #e2e8f0;border-radius:10px;padding:12px;text-align:center;break-inside:avoid}
      .qr-card img{display:block;margin:0 auto 8px}
      .unit-name{font-weight:800;font-size:13px;color:#0f2237;margin-bottom:2px}
      .unit-area{font-size:10px;color:#6b7c93;margin-bottom:4px}
      .unit-filter{font-family:monospace;font-size:11px;color:#2d6a9f;font-weight:700}
      @media print{
        @page{margin:12mm}
        body{padding:0}
      }
    </style></head><body>
    <h2>⚙ HVAC Filter Manager — QR Codes (${selectedUnits.length} units)</h2>
    <div class="grid">${cards}</div>
    <script>window.onload=()=>{setTimeout(()=>window.print(),800)}</script>
    </body></html>`

    printHTML(html)
  }

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(10,20,35,.75)',display:'flex',alignItems:'flex-end',justifyContent:'center',zIndex:999,backdropFilter:'blur(3px)'}}>
      <div style={{background:'#fff',borderRadius:'18px 18px 0 0',padding:'20px',width:'100%',maxWidth:640,maxHeight:'90vh',display:'flex',flexDirection:'column'}}>
        <div style={{width:40,height:4,background:'#e2e8f0',borderRadius:4,margin:'0 auto 16px'}}/>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
          <div>
            <h2 style={{fontSize:16,fontWeight:800,color:C.text}}>Print QR Codes</h2>
            <div style={{fontSize:11,color:C.muted}}>{selected.size} selected · {units.length} total units</div>
          </div>
          <button onClick={onClose} style={{background:'#f4f6f9',border:'none',borderRadius:8,padding:'6px 14px',cursor:'pointer',fontSize:12,color:C.muted}}>Close</button>
        </div>

        {/* Search + Building filter */}
        <div style={{display:'flex',gap:8,marginBottom:10,flexWrap:'wrap'}}>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍 Search units…"
            style={{flex:1,border:`1px solid ${C.border}`,borderRadius:8,padding:'8px 12px',fontSize:14,outline:'none'}}/>
          <select value={building} onChange={e=>setBuilding(e.target.value)}
            style={{border:`1px solid ${C.border}`,borderRadius:8,padding:'8px 12px',fontSize:14,outline:'none',background:'#fff'}}>
            <option value="all">All Buildings</option>
            {buildings.map(b=><option key={b} value={b}>{b}</option>)}
          </select>
        </div>

        {/* Select all / clear */}
        <div style={{display:'flex',gap:8,marginBottom:10,alignItems:'center'}}>
          <button onClick={selectAll} style={{background:C.steel,color:'#fff',border:'none',borderRadius:8,padding:'6px 14px',fontSize:12,fontWeight:600,cursor:'pointer'}}>Select All ({filtered.length})</button>
          <button onClick={clearAll} style={{background:'#f4f6f9',color:C.muted,border:'none',borderRadius:8,padding:'6px 14px',fontSize:12,fontWeight:600,cursor:'pointer'}}>Clear</button>
          <span style={{fontSize:11,color:C.muted,marginLeft:'auto'}}>{filtered.length} units shown</span>
        </div>

        {/* Unit list */}
        <div style={{flex:1,overflowY:'auto',display:'flex',flexDirection:'column',gap:6,marginBottom:14}}>
          {filtered.map(u=><QRCard key={u.id} unit={u} selected={selected.has(u.id)} onToggle={toggleUnit}/>)}
        </div>

        {/* Print button */}
        <button onClick={printQRs} disabled={selected.size===0}
          style={{width:'100%',padding:'14px',background:selected.size===0?'#ccc':C.navy,color:'#fff',border:'none',borderRadius:12,fontSize:14,fontWeight:700,cursor:selected.size===0?'not-allowed':'pointer'}}>
          🖨️ Print {selected.size>0?`${selected.size} QR Code${selected.size>1?'s':''}`:' QR Codes'} (PDF)
        </button>
      </div>
    </div>
  )
}
